import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFlash } from "@/hooks/useFlash";
import { useI18n } from "@/i18n";
import * as store from "@/lib/sheetStore";
import { getConsent } from "@/lib/mockAuth";
import { getFlags } from "@/lib/flags";

const CODE = {
  "sheet/version": "sheet.errVersion",
  "sheet/closed": "sheet.errClosed",
  "sheet/limit": "sheet.limit",
  "sheet/consent": "sheet.aiOff",
  "sheet/disabled": "sheet.errDisabled",
  "sheet/quota": "sheet.quota",
  "sheet/noread": "sheet.errNoread",
  "sheet/write": "sheet.failSave",
  "sheet/read": "sheet.failRead",
  "sheet/load": "sheet.failLoad",
  "sheet/missing": "sheet.failLoad",
};

const FIELD = {
  title: "sheet.errTitleMax",
  text: "sheet.errText",
  addText: "sheet.errAdd",
  entryDate: "sheet.errDate",
  question: "err.question",
  draftTitle: "err.title",
};

const empty = () => ({ title: "", kind: "bebas", entryDate: store.todayJakarta(), text: "" });
const snap = (s) => ({ title: s.title, kind: s.kind, entryDate: s.entryDate, text: store.userBlocks(s)[0]?.text ?? "" });

export function useSheet() {
  const { id } = useParams();
  const isNew = id === "baru";
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const [state, setState] = useState(isNew ? "ready" : "loading");
  const [sheet, setSheet] = useState(null);
  const [form, setForm] = useState(empty);
  const [saved, setSaved] = useState(empty);
  const [addOpen, setAddOpen] = useState(false);
  const [addText, setAddText] = useState("");
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [raised, setRaised] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState("");
  const [flash, setFlash] = useFlash();
  const guard = useRef(true);

  const adopt = useCallback((s) => {
    setSheet(s);
    setForm(snap(s));
    setSaved(snap(s));
  }, []);

  const load = useCallback(async () => {
    setState("loading");
    const res = await store.getSheet(id);
    if (res.error) return setState("error");
    adopt(res.sheet);
    setAddOpen(false);
    setAddText("");
    guard.current = true;
    setState("ready");
  }, [id, adopt]);

  useEffect(() => {
    if (!isNew) load();
  }, [isNew, load]);

  const run = useCallback(
    async (key, fn) => {
      setBusy(key);
      setErrors({});
      const res = await fn();
      setBusy("");
      if (res.error) {
        setErrors(res.error === "sheet/invalid" ? { [res.field]: t(FIELD[res.field] ?? "sheet.errText") } : { form: t(CODE[res.error] ?? "sheet.failSave") });
        return null;
      }
      return res;
    },
    [t],
  );

  const blocksUser = sheet ? store.userBlocks(sheet) : [];
  const allReadings = sheet ? store.readings(sheet) : [];
  const firstBlockId = blocksUser[0]?.id;
  const firstClosed = allReadings.some((r) => r.afterBlockId === firstBlockId);
  const last = sheet?.blocks[sheet.blocks.length - 1] ?? null;
  const lastReading = allReadings[allReadings.length - 1] ?? null;
  const hasQuestions = !!lastReading && !lastReading.abstained && lastReading.questions.length > 0;
  const left = sheet ? store.readingsLeft(sheet) : 2;
  const textValid = form.text.trim().length >= 20 && form.text.trim().length <= 6000;
  const dirty = JSON.stringify(form) !== JSON.stringify(saved) || (addOpen && addText.trim() !== "");

  const save = async () => {
    const local = {};
    if (form.title.length > 80) local.title = t("sheet.errTitleMax");
    if (!store.validDate(form.entryDate)) local.entryDate = t("sheet.errDate");
    if (!firstClosed && !textValid) local.text = t("sheet.errText");
    if (Object.keys(local).length) return setErrors(local);

    if (isNew) {
      const res = await run("save", () => store.createSheet(form));
      if (!res) return;
      setSaved(form);
      setFlash(t("sheet.saved"));
      guard.current = false;
      navigate(`/lembar/${res.id}`, { replace: true });
      return;
    }
    const res = await run("save", () =>
      store.updateSheet({
        id: sheet.id,
        version: sheet.version,
        title: form.title,
        kind: form.kind,
        entryDate: form.entryDate,
        blockId: firstClosed ? undefined : firstBlockId,
        text: firstClosed ? undefined : form.text,
      }),
    );
    if (!res) return;
    adopt(res.sheet);
    setFlash(t("sheet.saved"));
  };

  const saveAddition = async () => {
    const res = await run("add", () => store.addSheetBlock({ id: sheet.id, version: sheet.version, text: addText }));
    if (!res) return;
    adopt(res.sheet);
    setAddOpen(false);
    setAddText("");
    setFlash(t("sheet.saved"));
  };

  const requestReading = async () => {
    const res = await run("read", () => store.readSheet({ id: sheet.id, version: sheet.version, lang }));
    if (res) adopt(res.sheet);
  };

  const promote = async (payload) => {
    const res = await run("promote", () => store.promoteSheet({ id: sheet.id, ...payload }));
    if (!res) return null;
    adopt(res.sheet);
    setPromoteOpen(false);
    setFlash(t("sheet.promoted"));
    return res.decisionId;
  };

  const setStatus = async (status) => {
    const res = await run("status", () => store.setSheetStatus({ id: sheet.id, status }));
    if (res) {
      adopt(res.sheet);
      setFlash(t("sheet.saved"));
    }
  };

  const remove = async () => {
    const res = await run("delete", () => store.deleteSheet(sheet.id));
    if (res) {
      guard.current = false;
      navigate("/lembar", { replace: true });
    }
  };

  const openPromote = (question) => {
    setRaised(question ?? null);
    setPromoteOpen(true);
  };

  return {
    id, isNew, state, sheet, form, setForm, errors, setErrors, busy, flash, retry: load, guard,
    addOpen, setAddOpen, addText, setAddText, promoteOpen, setPromoteOpen, raised, openPromote,
    aiOn: !!getConsent()?.ai, aiPaused: getFlags().aiPaused,
    blocksUser, readings: allReadings, firstClosed, last, lastReading, hasQuestions, left,
    textValid, dirty, save, saveAddition, requestReading, promote, setStatus, remove,
  };
}
